/**
 * CapacityBadge
 *
 * Tiny inline pill that visualises the master backend's capacity
 * guard state. Sourced from ``useCapacityStatus`` (which polls
 * /api/system/capacity/ every 30s while the tab is visible).
 *
 * Renders nothing in the 'ok' or 'unknown' state — the chrome
 * stays clean for the 99% of the time the box is healthy. Only
 * appears when the backend is signalling soft warning ('warn')
 * or actively refusing work ('busy').
 *
 * Colour scheme follows the rest of the UI status pills
 * (Outlook, sync, etc.) — amber = heads-up, red = action.
 *
 * Hover surfaces a tooltip with which signals tripped + the
 * cool-down countdown so operators don't have to dig into the
 * Recent Issues panel for the gist of it.
 */

import { useMemo } from 'react'
import { describeTripped, useCapacityStatus } from '../hooks/useCapacityStatus'

export interface CapacityBadgeProps {
  /**
   * Place to mount the badge. ``inline`` is the default — fits
   * inside an existing toolbar / header. ``floating`` pins it
   * absolutely to the top-right of the page (used by the email
   * hub layout because the toolbar is already crowded).
   */
  variant?: 'inline' | 'floating'
}

export function CapacityBadge({ variant = 'inline' }: CapacityBadgeProps) {
  const { status, cooldownActiveSeconds, snapshot } = useCapacityStatus()

  // IMPORTANT: every hook in this component MUST be called above any
  // early return. The first version of this component returned null
  // before useMemo, which crashed every consumer (Email Hub layout)
  // with React error #310 the moment the master backend transitioned
  // from 'ok' → 'warn'/'busy', because the render after the
  // transition called one more hook than the previous render.
  // See: https://react.dev/errors/310
  //
  // ``details`` carries the LIVE value behind each tripped signal
  // (e.g. "Queue 1,820 / 1,500") so the tooltip can answer
  // "what was busy?" — not just name the resource.
  const details = useMemo(() => describeTripped(snapshot), [snapshot])

  // Hide entirely when nothing of interest is happening. This MUST
  // stay below every hook above.
  if (status === 'ok' || status === 'unknown') return null

  const enabledHint = !snapshot?.enabled
    ? ' (guard disabled — info only)'
    : ''

  // Multi-line tooltip: a headline + one "Resource value / limit" row
  // per tripped signal. Native title attr keeps it dependency-free and
  // accessible; the value/limit pairs are the "what was busy" detail.
  const headline = status === 'busy'
    ? (cooldownActiveSeconds > 0
        ? `System over capacity — new syncs paused ~${cooldownActiveSeconds}s. Existing syncs keep running.${enabledHint}`
        : `System over capacity — new syncs paused. Existing syncs keep running.${enabledHint}`)
    : `Heads-up: approaching capacity limits.${enabledHint}`
  const detailLines = details.map(d => `• ${d.label} ${d.valueText}`)
  const tooltip = [
    headline,
    detailLines.length ? '' : null,
    detailLines.length ? "What's busy (value / limit):" : null,
    ...detailLines,
  ].filter((l): l is string => l !== null).join('\n')

  // Floating variant: the old hardcoded `top-12` (48px) assumed the
  // app header is exactly that tall — when a banner (auth-lost, sync
  // error, env ribbon) pushes the chrome down, the badge overlapped
  // it (UX audit 2026-06, Layout #8). Shells that grow their top
  // chrome publish `--app-top-chrome-h`; the 48px fallback preserves
  // the historical position everywhere else.
  const baseClasses = variant === 'floating'
    ? 'fixed right-4 z-40'
    : 'inline-flex'
  const floatingStyle =
    variant === 'floating'
      ? { top: 'var(--app-top-chrome-h, 48px)' }
      : undefined

  const colourClasses = status === 'busy'
    ? 'bg-red-500/15 text-red-700 dark:text-red-300 border-red-500/40 hover:bg-red-500/25'
    : 'bg-amber-500/15 text-amber-700 dark:text-amber-300 border-amber-500/40 hover:bg-amber-500/25'

  // Surface the busiest resource right in the label so the operator
  // reads "what" at a glance without hovering; fall back to the
  // cooldown countdown when signals have already recovered.
  const topLabel = details[0]?.label
  const label = status === 'busy'
    ? (topLabel
        ? `Busy · ${topLabel}`
        : cooldownActiveSeconds > 0
          ? `Busy · ${cooldownActiveSeconds}s`
          : 'Busy')
    : (topLabel ? `Heads-up · ${topLabel}` : 'Heads-up')

  return (
    <span
      className={`${baseClasses} items-center gap-1.5 px-2 py-1 rounded-full border text-xs font-medium transition-colors ${colourClasses}`}
      style={floatingStyle}
      title={tooltip}
      role="status"
      aria-live="polite"
      aria-label={tooltip}
    >
      <span
        className={`h-1.5 w-1.5 rounded-full ${status === 'busy' ? 'bg-red-500' : 'bg-amber-500'}`}
        aria-hidden="true"
      />
      {label}
    </span>
  )
}
